import { type FC, type ReactNode } from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { message } from "antd";

import { StatusBadge } from "../shared";
import type { StatusBadgeTone } from "../shared";
import { AppStrings } from "../../utils/constants/AppStrings";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from "./affiliatesData";
import type { PromoCodeLinkStatus } from "../../types/affiliates";
import type { ShareableColumnId } from "./NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";

import styles from "./sharedPromoCodeColumns.module.css";

const LINK_STATUS_PRESENTATION: Record<
  PromoCodeLinkStatus,
  { tone: StatusBadgeTone; icon: ReactNode; label: string }
> = {
  provisioned: {
    tone: "success",
    icon: <CheckCircleOutlined />,
    label: AppStrings.APPLINK_READY,
  },
  failed: {
    tone: "danger",
    icon: <WarningOutlined />,
    label: AppStrings.APPLINK_FAILED,
  },
  pending: {
    tone: "warning",
    icon: <ClockCircleOutlined />,
    label: AppStrings.APPLINK_PENDING,
  },
  deleted: {
    tone: "neutral",
    icon: <DeleteOutlined />,
    label: AppStrings.APPLINK_DELETED,
  },
  not_applicable: {
    tone: "neutral",
    icon: <MinusCircleOutlined />,
    label: AppStrings.NOT_AVAILABLE,
  },
  unknown: {
    tone: "neutral",
    icon: <QuestionCircleOutlined />,
    label: AppStrings.UNKNOWN,
  },
};

const UNKNOWN_LINK_STATUS_PRESENTATION = {
  tone: "neutral",
  icon: <QuestionCircleOutlined />,
  label: AppStrings.UNKNOWN,
} satisfies { tone: StatusBadgeTone; icon: ReactNode; label: string };

export const LinkStatusBadge: FC<{
  status: PromoCodeLinkStatus | string;
}> = ({
  status,
}) => {
  const presentation =
    LINK_STATUS_PRESENTATION[status as PromoCodeLinkStatus] ??
    UNKNOWN_LINK_STATUS_PRESENTATION;
  return (
    <StatusBadge tone={presentation.tone} size="sm">
      {presentation.icon} {presentation.label}
    </StatusBadge>
  );
};

type AppLinkCopyCellProps = {
  url: string | null | undefined;
  linkStatus: PromoCodeLinkStatus | undefined;
};

export const AppLinkCopyCell: FC<AppLinkCopyCellProps> = ({
  url,
  linkStatus,
}) => {
  if (linkStatus !== "provisioned" || !url) return <>-</>;
  return (
    <div className={styles.appLinkCell}>
      <span className={styles.appLinkUrl} title={url}>
        {url}
      </span>
      <button
        type="button"
        className={styles.copyAppLink}
        onClick={() => {
          if (navigator.clipboard?.writeText) {
            void navigator.clipboard.writeText(url);
            message.success(AppStrings.APPLINK_COPIED);
          }
        }}
        aria-label={AppStrings.APPLINK_COPY}
      >
        <CopyOutlined aria-hidden="true" />
      </button>
    </div>
  );
};

// Shared column key — used by both AffiliatePromoCodes (always show all)
// and AffiliatePamView (filters by ShareableColumnId + always-on extras).
export type PromoCodeColumnKey =
  | "referralCode"
  | "totalUsers"
  | "totalFtds"
  | "ftdRate"
  | "cpaAmount"
  | "sportsbookPnl"
  | "casinoNgr"
  | "totalOwed"
  | "totalRevenue"
  | "status"
  | "reason"
  | "appLink";

export type PromoCodeColumnDefinition = Record<string, unknown> & {
  key: PromoCodeColumnKey;
};

// Metric column keys reused by the affiliates list table — same widths,
// renders and titles as the per-affiliate codes table so both views stay
// visually aligned.
const AFFILIATE_METRIC_KEYS: ReadonlyArray<PromoCodeColumnKey> = [
  "totalUsers",
  "totalFtds",
  "ftdRate",
  "cpaAmount",
  "sportsbookPnl",
  "casinoNgr",
  "totalOwed",
  "totalRevenue",
];

// Columns are built per-render with the active tenant currency symbol so
// table cells stay in sync with the platform config. Callers pull the
// symbol from `useCurrencySymbol()` and pass it in.
export const buildPromoCodeColumns = (
  currencySymbol: string,
): PromoCodeColumnDefinition[] => [
  {
    key: "referralCode",
    title: AppStrings.PROMO_CODE,
    dataIndex: "referralCode",
    width: 200,
    render: (referralCode: string | undefined) =>
      referralCode ? (
        <span className={styles.referralCode}>{referralCode}</span>
      ) : (
        "-"
      ),
  },
  {
    // Sits next to the code name — clients want the AppLink there by default.
    // Render BE `appsonair_link_url` (row.appLink), not the app deep link.
    key: "appLink",
    title: AppStrings.APPLINK,
    dataIndex: "appLink",
    width: 140,
    render: (
      url: string | null | undefined,
      row: { linkStatus?: PromoCodeLinkStatus },
    ) => <AppLinkCopyCell url={url} linkStatus={row.linkStatus} />,
  },
  {
    key: "totalUsers",
    title: AppStrings.TOTAL_USERS,
    dataIndex: "totalUsers",
    width: 120,
    render: (value: number | undefined) => formatNumber(value ?? 0),
  },
  {
    key: "totalFtds",
    title: AppStrings.TOTAL_FTDS,
    dataIndex: "totalFtds",
    width: 110,
    render: (value: number | undefined) => formatNumber(value ?? 0),
  },
  {
    key: "ftdRate",
    title: AppStrings.FTD_RATE,
    dataIndex: "ftdRate",
    width: 110,
    render: (value: number | undefined) => formatPercentage(value ?? 0),
  },
  {
    key: "cpaAmount",
    title: AppStrings.CPA_AMT,
    dataIndex: "cpaAmount",
    width: 120,
    // null → N/A (non-CPA plans); $0.00 means earned nothing on a CPA plan.
    render: (value: number | null | undefined) =>
      value == null
        ? AppStrings.NOT_AVAILABLE
        : formatCurrency(value, currencySymbol),
  },
  {
    key: "sportsbookPnl",
    title: AppStrings.SPORTSBOOK_PNL,
    dataIndex: "sportsbookPnl",
    width: 150,
    render: (value: number | undefined) =>
      formatCurrency(value ?? 0, currencySymbol),
  },
  {
    key: "casinoNgr",
    title: AppStrings.CASINO_GGR,
    dataIndex: "casinoNgr",
    width: 140,
    render: (value: number | undefined) =>
      formatCurrency(value ?? 0, currencySymbol),
  },
  {
    key: "totalOwed",
    title: AppStrings.TOTAL_OWED,
    dataIndex: "totalOwed",
    width: 130,
    render: (value: number | undefined) =>
      formatCurrency(value ?? 0, currencySymbol),
  },
  {
    key: "totalRevenue",
    title: AppStrings.TOTAL_REVENUE,
    dataIndex: "totalRevenue",
    width: 140,
    render: (value: number | undefined) => {
      const amount = value ?? 0;
      // Zero stays neutral — only color when there's an actual gain/loss.
      const className =
        amount > 0
          ? styles.revenuePositive
          : amount < 0
            ? styles.revenueNegative
            : undefined;
      return (
        <span className={className}>
          {formatCurrency(amount, currencySymbol)}
        </span>
      );
    },
  },
  {
    // Key aligns with the `status` ShareableColumnId so the Edit Affiliate
    // view toggle controls visibility. dataIndex stays `linkStatus` —
    // that's the actual row field with the AppsOnAir link state.
    key: "status",
    title: AppStrings.STATUS,
    dataIndex: "linkStatus",
    width: 140,
    render: (status: PromoCodeLinkStatus | string | undefined) =>
      status ? <LinkStatusBadge status={status} /> : null,
  },
  {
    key: "reason",
    title: AppStrings.REASON,
    dataIndex: "reason",
    width: 140,
    render: (reason: string | undefined) => (reason ? reason : "-"),
  },
];

export const buildAffiliateMetricColumns = (
  currencySymbol: string,
): PromoCodeColumnDefinition[] =>
  buildPromoCodeColumns(currencySymbol).filter((col) =>
    AFFILIATE_METRIC_KEYS.includes(col.key as PromoCodeColumnKey),
  );

// reason / appLink are operational columns shown regardless of the
// affiliate's portal_view_config selection — they're PAM-side context, not
// part of the 10 ShareableColumnIds the operator toggles. `status` is NOT
// here: it's driven by the `status` ShareableColumnId toggle.
export const ALWAYS_ON_COLUMNS: ReadonlyArray<PromoCodeColumnKey> = [
  "reason",
  "appLink",
];

// Single source of truth for "which promo-code columns are visible given a
// portal_view_config selection". Consumed by both the Edit Affiliate view
// preview (AffiliatePamView) and the operator's profile table
// (AffiliatePromoCodes) so the two never drift.
export const getVisiblePromoCodeColumns = (
  selectedColumns: ReadonlyArray<ShareableColumnId>,
  currencySymbol: string,
): PromoCodeColumnDefinition[] => {
  const visibleKeys = new Set<PromoCodeColumnKey>([
    ...(selectedColumns as ReadonlyArray<PromoCodeColumnKey>),
    ...ALWAYS_ON_COLUMNS,
  ]);
  return buildPromoCodeColumns(currencySymbol).filter((column) =>
    visibleKeys.has(column.key),
  );
};
